/**
 * Приведение ответов заливки тарифов к виду, удобному для показа.
 * Логика вынесена из компонентов, чтобы её можно было проверить тестами.
 */

/** Сводка предпросмотра: что нашлось в файле до записи. */
export const buildPreviewSummary = (preview) => {
  if (!preview) {
    return { ladderPoints: 0, routes: 0, sheets: 0, errors: 0, warnings: 0 };
  }
  return {
    ladderPoints: preview.ladderPoints ?? 0,
    routes: preview.routes ?? 0,
    sheets: preview.sheets?.length ?? 0,
    errors: preview.errors?.length ?? 0,
    warnings: preview.warnings?.length ?? 0,
  };
};

const MILE_ORDER = ["FIRST", "MIDDLE", "LAST", "INTRACITY"];

export const MILE_NAMES = {
  FIRST: "Первая миля",
  MIDDLE: "Магистраль",
  LAST: "Последняя миля",
  INTRACITY: "Внутригород",
};

/** Что заливка сделает с милей услуги. */
export const REPLACEMENT_ACTIONS = {
  replace: "заменится файлом",
  add: "добавится из файла",
  delete: "удалится: в файле её нет",
  keep: "не тронется",
};

/**
 * Что заливка сделает с тарифом услуги, по милям. Заливка замещает целиком
 * свою область — межгород или внутригород, — поэтому миля области, которой нет
 * в файле, удаляется, а мили вне области остаются как есть.
 *
 * @returns null, если предпросмотр снят без услуги
 */
export const buildReplacementTable = (preview) => {
  const replacement = preview?.replacement;
  if (!replacement) return null;

  const fileRoutes = {};
  (preview.sheets ?? []).forEach((sheet) => {
    fileRoutes[sheet.mileType] = (fileRoutes[sheet.mileType] ?? 0) + (sheet.routes ?? 0);
  });
  const current = {};
  [...(replacement.replaced ?? []), ...(replacement.kept ?? [])].forEach((count) => {
    current[count.mileType] = count;
  });
  const inScope = new Set(replacement.replacedMileTypes ?? []);

  const rows = MILE_ORDER.filter((mile) => current[mile]?.legs || fileRoutes[mile]).map((mile) => {
    const legs = current[mile]?.legs ?? 0;
    const inFile = fileRoutes[mile] ?? 0;
    let action = "keep";
    if (inScope.has(mile)) {
      if (!inFile) action = "delete";
      else action = legs ? "replace" : "add";
    }
    return {
      mileType: mile,
      name: MILE_NAMES[mile] ?? mile,
      legs,
      weightRanges: current[mile]?.weightRanges ?? 0,
      fileRoutes: inFile,
      action,
    };
  });

  const replaced = replacement.replaced ?? [];
  return {
    rows,
    deletedLegs: replaced.reduce((sum, count) => sum + (count.legs ?? 0), 0),
    deletedWeightRanges: replaced.reduce((sum, count) => sum + (count.weightRanges ?? 0), 0),
    lastImport: replacement.lastImport ?? null,
  };
};

/**
 * Итог заливки. Немонотонная цена и расхождения лестницы — разные вещи:
 * первое означает дефект расчёта, второе противоречие в самом тарифе.
 */
export const buildCommitSummary = (report) => {
  if (!report) {
    return {
      rowsApplied: 0,
      rejected: 0,
      routesCreated: 0,
      legsCreated: 0,
      weightRangesCreated: 0,
      deletedLegs: 0,
      importId: null,
      monotonicityViolations: 0,
      ladderInconsistencies: 0,
    };
  }
  return {
    rowsApplied: report.rowsApplied ?? 0,
    rejected: report.rejected?.length ?? 0,
    routesCreated: report.routesCreated ?? 0,
    legsCreated: report.legsCreated ?? 0,
    weightRangesCreated: report.weightRangesCreated ?? 0,
    deletedLegs: report.deletedLegs ?? 0,
    importId: report.importId ?? null,
    monotonicityViolations: report.probe?.monotonicityViolations ?? 0,
    ladderInconsistencies: report.probe?.ladderInconsistencies ?? 0,
  };
};

/**
 * Таблица контрольных точек: строка на ногу, колонка на вес.
 * Веса берутся из отчёта, а не собираются из точек, чтобы порядок колонок
 * не зависел от того, у какой ноги какие цены нашлись.
 */
export const buildProbeTable = (probe) => {
  if (!probe?.legs?.length) {
    return { weights: [], rows: [] };
  }

  const weights = (probe.weights ?? []).map((weight) => ({
    weight,
    // На точке лестницы расчёт идёт по отдельной ветке, поэтому промежуточные
    // веса надо смотреть отдельно — иначе проверка видит только половину.
    onLadder: probe.legs[0]?.points?.find((p) => sameNumber(p.weight, weight))?.onLadder ?? false,
  }));

  const rows = probe.legs.map((leg) => ({
    legId: leg.legId,
    mileType: leg.mileType,
    route: `${leg.originCity ?? leg.originKato} → ${leg.destinationCity ?? leg.destinationKato}`,
    dropped: new Set((leg.monotonicity ?? []).map((v) => String(v.weight))),
    prices: weights.map(({ weight }) => {
      const point = (leg.points ?? []).find((p) => sameNumber(p.weight, weight));
      return { weight, price: point?.price ?? null, onLadder: point?.onLadder ?? false };
    }),
  }));

  return { weights, rows };
};

const sameNumber = (a, b) => Number(a) === Number(b);

export const formatPrice = (value) =>
  value === null || value === undefined ? "—" : Number(value).toLocaleString("ru-RU");
